import type { GeoJsonObject } from 'geojson';
import { Icon, Marker as _Marker, TileLayer as TileLayerEntity } from 'leaflet';
import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Marker, Tooltip, Popup, CircleMarker } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import { toasting } from 'src/lib/components/Toast.component';
import { fget, fpost, fput } from 'src/lib/fetch';
import { DialogWaterLevel } from 'src/modules/mizusaki/components/DialogWaterLevel.component';
import '../../../map-dialog/components/WaterLevel.css';
import { setShowingDialogState } from 'src/redux/slices/showDialog.slice';
import { DeviceMarkerPopup } from './DeviceMarkerPopup.component';
import { PILOT_STATUS } from 'src/constants/pilotStatus';
import { DevicePrediction } from 'src/interfaces/DevicePrediction.interface';
import { fetchPredictions } from 'src/services/PredictionService';
import deviceCoordinatesData from 'src/data/device-coordinates.json';

const deviceOrder = [
  "DEVICE_1718433046201",
  "DEVICE_1717950746514",
  "DEVICE_1751587938731",
  "DEVICE_1746355078543",
  "DEVICE_1746355283533"
];

interface NodeMap {
  NODE_ID: string,
  TYPE: string,
  AREA_ID: string,
  POSITIONS: any,
  DATA: NodeMetadata
}
interface NodeMetadata {
  DEVICE_ID: { S: string };
  DEVICE_NAME: { S: string };
  WATER_LEVEL: { S: string };
  FLOW_RATE?: { S: string };
  VELOCITY?: { S: string };
  AREA_NAME?: { S: string };
  pilot_status?: string;
  predicted_time?: string;
  predicted_ranges?: number;
  passedTime?: string;
  passTimestamp?: string;
  isPilotCheck?: boolean;
  isPilotPassed?: boolean;
  is_client_flow?: boolean;
}

interface DeviceNodeData {
  AREA_ID: { S: string };
  DATA: { S: string };
  POSITIONS: { S: string };
  TYPE: { S: string };
  NODE_ID: { S: string };
}

const areaName = '香川用水';

interface PassedDot {
  deviceId: string;
  coordinate: { order: number; x: number; y: number };
  isCurrentPosition?: boolean; // Đánh dấu dot đại diện vị trí hiện tại
}

const directions = ['bottom', 'left', 'right']; // Danh sách các hướng

const getRandomDirection = () => {
  return directions[Math.floor(Math.random() * directions.length)];
};

export const CheckBoxWaterLevelMarkers = React.forwardRef(
  (
    {
      label = '水位',
      baseTileLayer,
      checked,
    }: {
      label?: string;
      baseTileLayer: React.RefObject<TileLayerEntity>;
      checked?: boolean;
    },
    ref: React.Ref<{ setGeoData?: (data: GeoJsonObject) => void }>,
  ) => {
    // Handle checkbox
    const eLabel = useRef<HTMLLabelElement>(null);
    const eInputLabel = useRef<HTMLDivElement>(null);

    const markersRef = useRef(new Map<string, _Marker>());
    const [firstParamForPrediction, setFirstParamForPrediction] = useState<any>(null);
    const [predictions, setPredictions] = useState<DevicePrediction | null>(null);
    const [nodeMap, setNodeMap] = useState<Map<string, NodeMap>>(new Map());

    const [position, setPosition] = useState<{
      data?: {
        marker: [number, number];
        pos: string;
        waterLevel: string;
        flowRate: string;
        deviceId: string;
        velocity: string;
        publish: string;
        pilot_status?: string;
        predicted_time?: string;
        predicted_ranges?: number;
        passedTime?: string;
        passTimestamp?: string;
        isPilotCheck?: boolean;
        areaName?: string;
      }[];
    }>({});
    const [dataDam, setDam] = useState<any>([]);
    const [passedCoordinates, setPassedCoordinates] = useState<PassedDot[]>([]);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Update first param for prediction
    useEffect(() => {
      const loadPredictions = async () => {
        try {
          if (!firstParamForPrediction) {
            console.log('[FirstParamForPrediction] No firstParamForPrediction, skipping prediction fetch');
            return;
          }
          const data = await fetchPredictions(firstParamForPrediction);
          setPredictions(data);
        } catch (error) {
          console.error('[FirstParamForPrediction] Failed to load predictions:', error);
        }
      };

      loadPredictions();
    }, [firstParamForPrediction, setFirstParamForPrediction]);

    const getDamData = async () => {
      try {
        const res = await fget('api/dams');
        const items = res?.[1]?.Items || [];
        const dams: any = [];
        items.forEach((item: any) => {
          const coo = item.DAM_COORDINATE.S.split(',');
          dams.push({
            marker: coo,
            DAM_NUMBER: item.DAM_NUMBER?.S ? item.DAM_NUMBER.S : '',
            RIVER_NAME: item.RIVER_NAME?.S ? item.RIVER_NAME.S : '',
            DAM_ID: item.DAM_ID.S,
          });
        });

        setDam(dams);
      } catch (e) {
        console.log(e);
        toasting({ children: 'データの取得に失敗しました。', containerProps: { className: 'border-red-600' } });
      }
    };


    // Function để cập nhật MIZUSAKI status cho device record
    const updateMizusakiStatus = async (deviceId: string, createAt: string, isMizusaki: boolean) => {
      try {
        const [error, response] = await fput<any, any>('/api/device/record/update-mizusaki', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: {
            deviceId,
            createAt,
            isMizusaki
          },
        });

        if (error) {
          console.error('[UpdateMizusaki] Failed to update MIZUSAKI status:', error);
        } else {
          console.log('[UpdateMizusaki] Successfully updated MIZUSAKI status:', response);
        }
      } catch (error) {
        console.error('[UpdateMizusaki] Error updating MIZUSAKI status:', error);
      }
    };

    const isPilotPassed = (deviceRecords: any) => {

      if (!deviceRecords || !deviceRecords.records || deviceRecords.records.length < 15) {
        return false;
      }

      console.log("[DeviceRecordFlowClientPage] device record: ", deviceRecords);

      const records = deviceRecords.records;

      // Extract water levels and create arrays
      const water_level = records.map((record: any) => parseFloat(record.WATER_LEVEL.S));
      // Chỉ check record đầu tiên (mới nhất) 
      const currentValue = water_level[deviceRecords.records.length - 1]; // Record đầu tiên (mới nhất)

      // xoá record cuối cùng của mảng water_level để tránh lấy giá trị hiện tại vào tính mean và std
      const poped_water_level = water_level;
      poped_water_level.pop();

      /**
       * Tính rolling mean cho một mảng
       * @param {Array} values - Mảng giá trị  
       * @param {number} windowSize - Kích thước cửa sổ
       * @returns {number} - Giá trị mean
       */
      function calculateMean(values: number[], windowSize: number): number {
        const window = values.slice(0, windowSize);
        console.log("Window for mean calculation:", window);

        return window.reduce((sum, val) => sum + val, 0) / window.length;
      }

      /**
       * Tính rolling standard deviation cho một mảng
       * @param {Array} values - Mảng giá trị
       * @param {number} windowSize - Kích thước cửa sổ
       * @param {number} mean - Giá trị mean tương ứng
       * @returns {number} - Giá trị std
       */
      function calculateStd(values: number[], windowSize: number, mean: number): number {
        const window = values.slice(0, windowSize);
        const variance = window.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / window.length;
        console.log("Window for std calculation:", window);
        console.log("Variance for std calculation:", variance);

        return Math.sqrt(variance);
      }

      // Parameters for sigma algorithm
      const windowN = 30; // Window size for rolling calculation từ 30 records trước đó
      const sigma = 2.75; // Sigma multiplier for band calculation

      // Tính mean và std từ 30 records gần nhất (từ index 0 đến 29)
      const recentValues = poped_water_level.slice(0, windowN);
      const rollMean = calculateMean(recentValues, windowN);
      const rollStd = calculateStd(recentValues, windowN, rollMean);

      const upperBand = rollMean + sigma * rollStd;
      const lowerBand = rollMean - sigma * rollStd;

      const mizusakiUpSigma = currentValue > upperBand;
      const mizusakiDownSigma = currentValue < lowerBand;

      const mizusakiDetected = mizusakiUpSigma || mizusakiDownSigma;

      // Debug logs
      console.log("[DeviceRecordFlowClientPage] Current water level (latest record):", currentValue);
      console.log("[DeviceRecordFlowClientPage] Rolling mean (20 records):", rollMean);
      console.log("[DeviceRecordFlowClientPage] Rolling std (20 records):", rollStd);
      console.log("[DeviceRecordFlowClientPage] Upper band:", upperBand, "Lower band:", lowerBand);
      console.log("[DeviceRecordFlowClientPage] MIZUSAKI detected:", mizusakiDetected);

      if (mizusakiDetected) {
        const firstParam = records[deviceRecords.records.length - 1]; // Record đầu tiên (mới nhất)
        const beforefirstParam = records[deviceRecords.records.length - 2]; // Record thứ hai

        const passTimestamp = firstParam?.CREATE_AT?.S;

        console.log("[DeviceRecordFlowClientPage] 水先 detected on latest record, firstParam:", firstParam);
        console.log("[DeviceRecordFlowClientPage] Checking beforefirstParam for IS_MIZUSAKI:", beforefirstParam);
        console.log("[DeviceRecordFlowClientPage] beforefirstParam.IS_MIZUSAKI:", beforefirstParam?.IS_MIZUSAKI);
        console.log("[DeviceRecordFlowClientPage] 水先 timestamp:", passTimestamp);

        // Cập nhật IS_MIZUSAKI = true cho record này
        if (firstParam?.DEVICE_ID?.S && passTimestamp) {
          updateMizusakiStatus(firstParam.DEVICE_ID.S, passTimestamp, true);
        }

        if (beforefirstParam?.IS_MIZUSAKI?.BOOL === true) {
          // Nếu record thứ hai cũng có IS_MIZUSAKI = true, thực hiện hành động tương ứng
          console.log("[DeviceRecordFlowClientPage] ✅ CONFIRMED: Previous record also has IS_MIZUSAKI = true");
          return {
            ispilotPass: true,
            firstParam,
            passTimestamp
          };
        } else {
          // Nếu record trước không có IS_MIZUSAKI = true
          console.log("[DeviceRecordFlowClientPage] ❌ Previous record does not have IS_MIZUSAKI = true:", beforefirstParam?.IS_MIZUSAKI);
          console.log("[DeviceRecordFlowClientPage] Not confirming mizusaki detection - need consecutive mizusaki records");
        }
      }

      // If no MIZUSAKI found
      console.log("[DeviceRecordFlowClientPage] Không tìm thấy 水先 on latest record.");
      return {
        ispilotPass: false,
        firstParam: null,
        passTimestamp: null
      };
    };

    // fetch device records data
    useEffect(() => {
      const fetchDeviceRecordData = async () => {
        try {

          const [__, deviceRecords] = await fget(
            `api/device-records-flow?deviceIds=DEVICE_1717950746514,DEVICE_1718433046201,DEVICE_1746355078543,DEVICE_1746355283533,DEVICE_1746355562363,DEVICE_1751587938731`,
          );


          const devicesArray = Object.entries(deviceRecords).map(([id, records]) => ({
            id,
            records,
          }));

          // Wait for nodes to be loaded before updating device nodes
          nodeMap.forEach((node: NodeMap) => {
            const deviceRecords: any = devicesArray.find((record: any) => {
              const deviceId = record.id;
              return deviceId === node.NODE_ID;
            });

            const nodeMetadata: NodeMetadata = node.DATA;
            if (node.DATA.isPilotCheck) {
              const pilotResult = isPilotPassed(deviceRecords);

              if (pilotResult && pilotResult.ispilotPass && nodeMetadata.pilot_status == PILOT_STATUS.NOT_PASS) {
                setFirstParamForPrediction((prevValue: any) => {
                  return pilotResult.firstParam;
                });

                // change status node
                nodeMetadata.isPilotPassed = true;
                nodeMetadata.pilot_status = PILOT_STATUS.CONFIRMING;
                nodeMetadata.passedTime = new Date().toISOString();
                nodeMetadata.passTimestamp = pilotResult.passTimestamp;

                // Gọi API để cập nhật node
                const nodesToSave = [
                  {
                    AREA_NAME: areaName,
                    NODE_ID: node.NODE_ID,
                    POSITIONS: `${node.POSITIONS.x},${node.POSITIONS.y}`,
                    DATA: nodeMetadata,
                    TYPE: node.TYPE,
                    CREATED_DATE: { S: Date.now() },
                  },
                ];

                fput(`/api/areas/nodes/${areaName}`, {
                  body: {
                    nodes: nodesToSave,
                  },
                })
                  .catch((error) => {
                    console.error(`Failed to update node ${node.NODE_ID}:`, error);
                  });
              }
            }
          });

        } catch (error) {
          console.error('Error fetching data:', error);
        }
      };

      fetchDeviceRecordData();

      // const interval = setInterval(fetchDeviceRecordData, 20000); // Run every 20 seconds

      // return () => clearInterval(interval); // Cleanup on unmount
    }, [predictions]);

    const setPositionWithPrediction = useCallback(async (items: any, nodeMapTemp: Map<any, any>) => {
      console.log("[SetPosition] Setting initial position data from items: ", items);
      
      const positions: any = [];

      for (const item of items) {
        // Get node data for this device (includes pilot_status, predicted_time, etc.)
        const nodeInfo = nodeMapTemp.get(item.DEVICE_ID.S);
        if (!nodeInfo || !nodeInfo.DATA) continue;

        const coo = item.DEVICE_COORDINATE.S.split(',');

        // Không gán predicted_time và predicted_ranges ở đây
        // Chúng sẽ được merge sau khi predictions có dữ liệu qua useEffect riêng
        positions.push({
          marker: coo,
          pos: item.DEVICE_NAME?.S ? item.DEVICE_NAME.S : '',
          waterLevel: nodeInfo?.DATA.WATER_LEVEL?.S || '0',
          deviceId: item.DEVICE_ID.S,
          // Add pilot status information from node data
          pilot_status: nodeInfo.DATA.pilot_status,
          passedTime: nodeInfo.DATA.passedTime,
          passTimestamp: nodeInfo.DATA.passTimestamp,
          isPilotCheck: nodeInfo.DATA.isPilotCheck,
          areaName: areaName,
        });
      }
      console.log("[SetPosition] Initial positions set (without predictions): ", positions);
      
      setPosition({ data: positions });
    }, [nodeMap]);

    const getDeviceData = async () => {
      try {
        const res = await fget('api/devices');
        const items = res?.[1]?.Items || [];

        // Fetch node data from areas API to get pilot status information
        const areaName = '香川用水';
        const [, nodeData] = await fget<any, any[]>(`/api/areas/${areaName}/nodes`);
        const devicePivotPassed: any = [];

        // Create a map of device nodes by DEVICE_ID for quick lookup
        const nodeMapTemp = new Map();
        if (nodeData) {
          nodeData.forEach((node: DeviceNodeData) => {
            if (node.TYPE?.S === 'device') {
              const data: NodeMetadata = JSON.parse(node.DATA?.S || '{}');
              nodeMapTemp.set(data.DEVICE_ID?.S, {
                NODE_ID: node.NODE_ID.S,
                TYPE: node.TYPE.S,
                AREA_ID: node.AREA_ID.S,
                POSITIONS: node.POSITIONS.S,
                DATA: data
              });

              if (data.pilot_status === PILOT_STATUS.PASSED) {
                devicePivotPassed.push({
                  device_id: data.DEVICE_ID?.S,
                  create_at: data.passTimestamp
                });
              }
            }
          });
        }
        setNodeMap(nodeMapTemp);

        if (devicePivotPassed.length > 0) {
          // Tìm deviceId có thứ tự lớn nhất trong deviceOrder
          let maxIndex = -1;
          let selectedDeviceId: any = null;
          let create_at: any = null;

          devicePivotPassed.forEach((device: any) => {
            const idx = deviceOrder.indexOf(device.device_id);

            if (idx > maxIndex) {
              maxIndex = idx;
              selectedDeviceId = device.device_id;
              create_at = device.create_at;
            }
          });

          // Gọi API để lấy device records
          try {
            const [_, deviceRecord] = await fget<any, any>(`/api/device/record?deviceId=${selectedDeviceId}&createAt=${create_at}`);

            if (deviceRecord) {
              setFirstParamForPrediction(deviceRecord);
            }
          } catch (error) {
            const nodeForPredicted: NodeMap = nodeMapTemp.get(selectedDeviceId);

            if (nodeForPredicted) {
              setFirstParamForPrediction({
                DEVICE_ID: {
                  S: selectedDeviceId
                },
                PUBLISH_FORMATED: {
                  S: nodeForPredicted.DATA.passedTime || '',
                },
              });
            }
            // console.error('[FirstParamForPrediction] Failed to fetch device records:', error);
          }
        }

        setPositionWithPrediction(items, nodeMapTemp);
      } catch (e) {
        console.log("error", e);
        toasting({ children: 'データの取得に失敗しました。', containerProps: { className: 'border-red-600' } });
      }
    };

    useEffect(() => {
      const fetchData = async () => {
        await Promise.all([getDamData(), getDeviceData()]);
      };
      fetchData();
      // // Call getDeviceData every 30 seconds
      const intervalId = setInterval(async () => {
        await Promise.all([getDeviceData()]);
      }, 60000);
      return () => {
        clearInterval(intervalId);
      };
    }, [refreshTrigger]);

    const [inputChecked, setInputChecked] = useState<boolean>(!!checked);
    const [flowRateChecked, setFlowChecked] = useState<boolean>(false);
    const [velocityChecked, setVelocityChecked] = useState<boolean>(false);

    const dispatch = useDispatch();

    // Handle ref
    useImperativeHandle(ref, () => ({}));

    // Callback để refresh data khi GraphSettingsPopup update thành công
    const handleDataRefresh = useCallback(() => {
      setRefreshTrigger(prev => prev + 1);
    }, []);

    // Effect để merge prediction vào position khi predictions thay đổi
    useEffect(() => {
      if (!predictions || !position.data || position.data.length === 0) {
        return;
      }

      const newPositions = position.data.map(posData => {
        const predictionByDeviceId = predictions[posData.deviceId];
        if (predictionByDeviceId) {
          return {
            ...posData,
            predicted_time: predictionByDeviceId.predicted_time,
            predicted_ranges: predictionByDeviceId.predicted_ranges,
          };
        }
        return posData;
      });

      console.log('[MergePrediction] Updated positions:', newPositions);
      setPosition({ data: newPositions });
    }, [predictions]);

    // Effect để tính toán các dots mà mizusaki đã đi qua
    useEffect(() => {
      const calculatePassedCoordinates = () => {
        console.log('[PassedDots] Calculating passed coordinates...');
        const result: PassedDot[] = [];
        
        for (let i = 0; i < deviceOrder.length; i++) {
          const currentDeviceId = deviceOrder[i];
          const nextDeviceId = deviceOrder[i + 1];
          
          const currentNode = nodeMap.get(currentDeviceId);
          if (!currentNode?.DATA) {
            console.log(`[PassedDots] No node data for ${currentDeviceId}, skipping`);
            continue;
          }
          
          const currentCoords = deviceCoordinatesData.devices
            .find(d => d.deviceId === currentDeviceId)
            ?.coordinates || [];
          
          if (currentCoords.length === 0) {
            console.log(`[PassedDots] No coordinates for ${currentDeviceId}, skipping`);
            continue;
          }
          
          // Case 1: Node hiện tại đã passed
          if (currentNode.DATA.isPilotPassed || currentNode.DATA.pilot_status === PILOT_STATUS.PASSED) {
            const nextNode = nextDeviceId ? nodeMap.get(nextDeviceId) : null;
            
            console.log(`[PassedDots] ${currentDeviceId} has passed, checking next node...`);
            
            // Sub-case 1a: Node tiếp theo cũng đã passed → 100%
            if (nextNode?.DATA?.isPilotPassed || nextNode?.DATA?.pilot_status === PILOT_STATUS.PASSED) {
              console.log(`[PassedDots] Next node ${nextDeviceId} also passed, rendering all dots`);
              currentCoords.forEach(coord => {
                result.push({ 
                  deviceId: currentDeviceId, 
                  coordinate: coord,
                  isCurrentPosition: false 
                });
              });
              continue;
            }
            
            // Sub-case 1b: Node tiếp theo chưa passed → tính % theo thời gian
            const passedTime = currentNode.DATA.passedTime;
            if (!passedTime) {
              console.log(`[PassedDots] No passedTime for ${currentDeviceId}, skipping`);
              continue;
            }
            
            const T_start = new Date(passedTime).getTime();
            const T_now = Date.now();
            
            // Lấy predicted time và range từ PREDICTIONS state theo deviceId
            const prediction = predictions?.[nextDeviceId || ''];
            console.log("[PassedDots] Predictions state:", predictions);
            console.log("[PassedDots] Fetched prediction for", currentDeviceId, ":", predictions);
            
            const predictedTimeStr = prediction?.predicted_time;
            const predictedRange = prediction?.predicted_ranges || 0;
            
            console.log(`[PassedDots] ${currentDeviceId} prediction:`, { predictedTimeStr, predictedRange });
            
            if (!predictedTimeStr) {
              console.log(`[PassedDots] No predicted time for ${currentDeviceId}, skipping`);
              continue;
            }
            
            // Tính thời gian dự kiến thực tế = predicted_time + (predicted_range / 2)
            const T_predicted = new Date(predictedTimeStr).getTime() 
                              + (predictedRange / 2) * 60 * 1000;
            
            const totalDuration = T_predicted - T_start;
            const elapsedTime = T_now - T_start;
            
            console.log(`[PassedDots] ${currentDeviceId} timing:`, {
              T_start: new Date(T_start),
              T_now: new Date(T_now),
              T_predicted: new Date(T_predicted),
              totalDuration: totalDuration / 1000 / 60 + ' minutes',
              elapsedTime: elapsedTime / 1000 / 60 + ' minutes'
            });
            
            // Tính % tiến trình
            const progress = Math.max(0, Math.min(1, elapsedTime / totalDuration));
            
            // Có 10 dots, tính số dots đã qua
            const totalDots = currentCoords.length;
            const exactDotProgress = progress * totalDots;
            const dotsToRender = Math.floor(exactDotProgress);
            
            console.log(`[PassedDots] ${currentDeviceId} progress:`, {
              progress: (progress * 100).toFixed(2) + '%',
              exactDotProgress,
              dotsToRender
            });
            
            // Vẽ các dots đã qua hoàn toàn
            for (let j = 0; j < dotsToRender; j++) {
              result.push({ 
                deviceId: currentDeviceId, 
                coordinate: currentCoords[j],
                isCurrentPosition: false 
              });
            }
          } else {
            console.log(`[PassedDots] ${currentDeviceId} has not passed yet, skipping`);
          }
        }
        
        console.log(`[PassedDots] Total dots to render: ${result.length}`, result);
        setPassedCoordinates(result);
      };
      
      calculatePassedCoordinates();
      
      // Update mỗi 5 giây để cập nhật tiến trình real-time
      const interval = setInterval(calculatePassedCoordinates, 10000);
      return () => clearInterval(interval);
      
    }, [nodeMap, predictions]);

    return (
      <>
        <div className="flex" ref={eInputLabel}>
          <input
            type="checkbox"
            id={'flow_rate'}
            checked={flowRateChecked}
            onChange={(e) => {
              setFlowChecked(e.target.checked);
            }}
          />
          <label className="ml-2" htmlFor={'flow_rate'}>
            流量
          </label>
        </div>

        <div className="flex" ref={eInputLabel}>
          <input
            type="checkbox"
            id={'velocity'}
            checked={velocityChecked}
            onChange={(e) => {
              setVelocityChecked(e.target.checked);
            }}
          />
          <label className="ml-2" htmlFor={'velocity'} ref={eLabel}>
            {'流速'}
          </label>
        </div>

        <div className="flex" ref={eInputLabel}>
          <input
            type="checkbox"
            id={label}
            checked={inputChecked}
            onChange={(e) => {
              setInputChecked(e.target.checked);
            }}
          />
          <label className="ml-2" htmlFor={label} ref={eLabel}>
            {label}
          </label>
        </div>

        {(inputChecked || flowRateChecked || velocityChecked) &&
          dataDam?.map((position: any, index: number) => (
            <Marker
              key={`dam-marker-${index}`}
              position={position.marker}
              icon={
                new Icon({
                  iconUrl: 'assets/dam.png',
                  iconSize: [32, 32],
                  iconAnchor: [16, 32],
                  popupAnchor: [16, -16],
                  tooltipAnchor: [16, -16],
                })
              }
              ref={(e) => {
                if (e) markersRef.current.set(position.deviceId, e);
              }}>
              <Tooltip sticky className="z-10" permanent={true}>
                ダム:{position.DAM_NUMBER}
              </Tooltip>
            </Marker>
          ))}

        {
          position?.data?.map((position, idx) => (
            <Marker
              ref={(e) => {
                if (e) {
                  markersRef.current.set(position.deviceId, e);
                }
              }}
              icon={
                new Icon({
                  iconUrl: 'assets/marker-icon.png',
                  shadowUrl: 'assets/marker-shadow.png',
                  iconSize: [25, 41],
                  iconAnchor: [13, 41],
                  popupAnchor: [2, -40],
                })
              }
              key={`marker-${idx}`}
              position={position.marker}
            >
              <Tooltip
                direction="top"
                permanent={true}
                interactive={true}
                opacity={1}
                eventHandlers={{
                  click: () => {
                    dispatch(
                      setShowingDialogState({
                        showDialogWaterLevel: true,
                        position: position,
                        isMap: true,
                        isWaterLevel: inputChecked,
                        isFlowRate: flowRateChecked,
                        isVelocity: velocityChecked,
                      }),
                    );
                  },
                }}
              >
                <DeviceMarkerPopup
                  data={(() => {
                    console.log("[DeviceMarkerPopup] Position data:", position);
                    return {
                      DEVICE_ID: { S: position.deviceId },
                      DEVICE_NAME: { S: position.pos },
                      WATER_LEVEL: { S: position.waterLevel },
                      FLOW_RATE: { S: position.flowRate },
                      VELOCITY: { S: position.velocity },
                      AREA_NAME: { S: position.areaName || '香川用水' },
                      pilot_status: position.pilot_status,
                      predicted_time: position.predicted_time,
                      predicted_ranges: position.predicted_ranges,
                      passedTime: position.passedTime,
                      passTimestamp: position.passTimestamp,
                      isPilotCheck: position.isPilotCheck,
                    };
                  })()}
                  onDataUpdate={handleDataRefresh}
                />
              </Tooltip>
              <Popup maxWidth={700} closeButton={false} >
                <DialogWaterLevel />
              </Popup>
            </Marker>
          ))}

        {/* Render passed coordinate dots (mizusaki đã đi qua) */}
        {
          passedCoordinates.map((dot, idx) => (
            <CircleMarker
              key={`passed-dot-${dot.deviceId}-${dot.coordinate.order}-${idx}`}
              center={[dot.coordinate.y, dot.coordinate.x]}
              radius={6}
              pathOptions={{
                color: '#ff0000' ,
                fillColor: '#ff0000',
                fillOpacity: 0.9,
                weight: 2
              }}
            >
              <Tooltip permanent={false} direction="top">
                {`Point ${Math.floor(dot.coordinate.order)}`}
              </Tooltip>
            </CircleMarker>
          ))}

      </>
    );
  },
);
